import { useEffect, useState } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiAlertTriangle, FiPackage } from 'react-icons/fi';
import { apiRequest } from '../utils/auth';
import { useEscapeToClose } from '../utils/useEscapeToClose';
import Pagination from '../components/Pagination';

interface Version {
  _id: string;
  version: string;
  build: string;
  forceUpdate: boolean;
  platforms: string[];
  createdAt: string;
  updatedAt: string;
}

interface PaginationState {
  total: number;
  limit: number;
  skip: number;
}

interface VersionForm {
  version: string;
  build: string;
  forceUpdate: boolean;
  platforms: string[];
}

const emptyForm: VersionForm = { version: '', build: '', forceUpdate: false, platforms: ['ios', 'android'] };

export default function VersionControlPage() {
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({ total: 0, limit: 20, skip: 0 });
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<VersionForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Escape backs out of the form (discarding the draft, like Cancel) and of the
  // delete confirmation.
  useEscapeToClose(showModal, () => { setShowModal(false); setForm(emptyForm); setEditingId(null); });
  useEscapeToClose(!!deleteId, () => setDeleteId(null));

  const fetchVersions = async (skip = 0) => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ limit: '20', skip: skip.toString() });
      const response = await apiRequest<{
        success: boolean;
        versions: Version[];
        total: number;
        limit: number;
        skip: number;
      }>(`/api/admin/versions?${params}`);
      setVersions(response.versions);
      setPagination({ total: response.total, limit: response.limit, skip: response.skip });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch versions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVersions(0); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (v: Version) => {
    setEditingId(v._id);
    setForm({ version: v.version, build: v.build, forceUpdate: v.forceUpdate, platforms: v.platforms || ['ios', 'android'] });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.version.trim() || !form.build.trim()) {
      alert('Version and Build are required');
      return;
    }
    if (form.platforms.length === 0) {
      alert('At least one platform must be selected');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await apiRequest(`/api/admin/versions/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify(form),
        });
      } else {
        await apiRequest('/api/admin/versions', {
          method: 'POST',
          body: JSON.stringify(form),
        });
      }
      setShowModal(false);
      setForm(emptyForm);
      setEditingId(null);
      fetchVersions(pagination.skip);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save version');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await apiRequest(`/api/admin/versions/${deleteId}`, { method: 'DELETE' });
      setDeleteId(null);
      fetchVersions(pagination.skip);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete version');
    }
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;

  return (
    <div className="page">
      <div style={{ marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Version Control</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => fetchVersions(0)}
            style={{ padding: '8px 16px', border: '1px solid var(--color-border)', borderRadius: '4px', backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text)', cursor: 'pointer', fontSize: '14px' }}
          >
            Refresh
          </button>
          <button
            onClick={openCreate}
            style={{ padding: '8px 16px', backgroundColor: 'var(--color-success-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <FiPlus size={16} /> Add Version
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px', backgroundColor: 'var(--color-danger-surface)', color: 'var(--color-danger)', borderRadius: '4px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-muted)' }}>Loading versions...</div>
      ) : versions.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-muted)' }}>
          <FiPackage size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <p>No versions found</p>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '12px', color: 'var(--color-muted)', fontSize: '14px' }}>
            Showing {versions.length} of {pagination.total} versions
          </div>
          {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {versions.map((v) => {
              const platforms = v.platforms || ['ios', 'android'];
              return (
                <div key={v._id} style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', minWidth: 0 }}>
                      <span style={{ fontWeight: 600, color: 'var(--color-text)', fontSize: '16px' }}>{v.version}</span>
                      <span style={{ color: 'var(--color-muted)', fontFamily: 'monospace', fontSize: '13px' }}>build {v.build}</span>
                    </div>
                    {v.forceUpdate ? (
                      <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: 'var(--color-danger-strong)', color: '#fff', fontSize: '12px', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                        <FiAlertTriangle size={12} /> Force
                      </span>
                    ) : (
                      <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: 'var(--color-success-strong)', color: '#fff', fontSize: '12px', fontWeight: 500, flexShrink: 0 }}>Optional</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {platforms.includes('ios') && <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: 'var(--color-accent-strong)', color: '#fff', fontSize: '12px', fontWeight: 500 }}>iOS</span>}
                    {platforms.includes('android') && <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: 'var(--color-success-strong)', color: '#fff', fontSize: '12px', fontWeight: 500 }}>Android</span>}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--color-muted)' }}>
                    <div>Created: {formatDate(v.createdAt)}</div>
                    <div>Updated: {formatDate(v.updatedAt)}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', borderTop: '1px solid var(--color-border)', paddingTop: '10px' }}>
                    <button onClick={() => openEdit(v)} style={{ flex: 1, padding: '8px 12px', backgroundColor: 'var(--color-accent-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      <FiEdit2 size={14} /> Edit
                    </button>
                    <button onClick={() => setDeleteId(v._id)} style={{ flex: 1, padding: '8px 12px', backgroundColor: 'var(--color-danger-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      <FiTrash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          ) : (
          <div style={{ backgroundColor: 'var(--color-surface)', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: 'var(--color-surface-hover)' }}>
                <tr>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Version</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Build</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Force Update</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Platforms</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Created</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Updated</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {versions.map((v) => (
                  <tr key={v._id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '12px', fontWeight: 600, color: 'var(--color-text)', fontSize: '14px' }}>{v.version}</td>
                    <td style={{ padding: '12px', color: 'var(--color-text)', fontFamily: 'monospace', fontSize: '14px' }}>{v.build}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {v.forceUpdate ? (
                        <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: 'var(--color-danger-strong)', color: '#fff', fontSize: '12px', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <FiAlertTriangle size={12} /> Yes
                        </span>
                      ) : (
                        <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: 'var(--color-success-strong)', color: '#fff', fontSize: '12px', fontWeight: 500 }}>
                          No
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                        {(v.platforms || ['ios', 'android']).includes('ios') && (
                          <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: 'var(--color-accent-strong)', color: '#fff', fontSize: '12px', fontWeight: 500 }}>
                            iOS
                          </span>
                        )}
                        {(v.platforms || ['ios', 'android']).includes('android') && (
                          <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: 'var(--color-success-strong)', color: '#fff', fontSize: '12px', fontWeight: 500 }}>
                            Android
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: 'var(--color-muted)' }}>{formatDate(v.createdAt)}</td>
                    <td style={{ padding: '12px', fontSize: '13px', color: 'var(--color-muted)' }}>{formatDate(v.updatedAt)}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                        <button
                          onClick={() => openEdit(v)}
                          style={{ padding: '4px 10px', backgroundColor: 'var(--color-accent-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                          <FiEdit2 size={14} /> Edit
                        </button>
                        <button
                          onClick={() => setDeleteId(v._id)}
                          style={{ padding: '4px 10px', backgroundColor: 'var(--color-danger-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                          <FiTrash2 size={14} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => fetchVersions((page - 1) * pagination.limit)}
          />
        </>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'var(--color-surface)', borderRadius: '8px', padding: '24px', width: '420px', maxWidth: '90vw', border: '1px solid var(--color-border)' }}>
            <h3 style={{ margin: '0 0 16px', color: 'var(--color-text)' }}>{editingId ? 'Edit Version' : 'Add Version'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: 'var(--color-muted)' }}>Version</label>
                <input
                  value={form.version}
                  onChange={(e) => setForm({ ...form, version: e.target.value })}
                  placeholder="e.g. 1.3.0"
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--color-border)', backgroundColor: 'var(--color-input)', color: 'var(--color-text)', fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: 'var(--color-muted)' }}>Build Number</label>
                <input
                  value={form.build}
                  onChange={(e) => setForm({ ...form, build: e.target.value })}
                  placeholder="e.g. 130"
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--color-border)', backgroundColor: 'var(--color-input)', color: 'var(--color-text)', fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={form.forceUpdate}
                  onChange={(e) => setForm({ ...form, forceUpdate: e.target.checked })}
                  id="forceUpdate"
                  style={{ width: '16px', height: '16px' }}
                />
                <label htmlFor="forceUpdate" style={{ fontSize: '14px', color: 'var(--color-text)', cursor: 'pointer' }}>
                  Force Update
                </label>
                <span style={{ fontSize: '12px', color: 'var(--color-muted)' }}>
                  (Users must update to continue using the app)
                </span>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: 'var(--color-muted)' }}>Platforms</label>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={form.platforms.includes('ios')}
                      onChange={(e) => {
                        const newPlatforms = e.target.checked
                          ? [...form.platforms, 'ios']
                          : form.platforms.filter(p => p !== 'ios');
                        setForm({ ...form, platforms: newPlatforms });
                      }}
                      id="platformIos"
                      style={{ width: '16px', height: '16px' }}
                    />
                    <label htmlFor="platformIos" style={{ fontSize: '14px', color: 'var(--color-text)', cursor: 'pointer' }}>
                      iOS
                    </label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={form.platforms.includes('android')}
                      onChange={(e) => {
                        const newPlatforms = e.target.checked
                          ? [...form.platforms, 'android']
                          : form.platforms.filter(p => p !== 'android');
                        setForm({ ...form, platforms: newPlatforms });
                      }}
                      id="platformAndroid"
                      style={{ width: '16px', height: '16px' }}
                    />
                    <label htmlFor="platformAndroid" style={{ fontSize: '14px', color: 'var(--color-text)', cursor: 'pointer' }}>
                      Android
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={() => { setShowModal(false); setForm(emptyForm); setEditingId(null); }}
                style={{ padding: '8px 16px', border: '1px solid var(--color-border)', borderRadius: '4px', backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text)', cursor: 'pointer', fontSize: '14px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{ padding: '8px 16px', backgroundColor: 'var(--color-success-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
              >
                {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'var(--color-surface)', borderRadius: '8px', padding: '24px', width: '380px', maxWidth: '90vw', border: '1px solid var(--color-border)' }}>
            <h3 style={{ margin: '0 0 12px', color: 'var(--color-text)' }}>Delete Version</h3>
            <p style={{ color: 'var(--color-muted)', fontSize: '14px', margin: '0 0 20px' }}>
              Are you sure you want to delete this version? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeleteId(null)}
                style={{ padding: '8px 16px', border: '1px solid var(--color-border)', borderRadius: '4px', backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text)', cursor: 'pointer', fontSize: '14px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                style={{ padding: '8px 16px', backgroundColor: 'var(--color-danger-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
